import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import UndoFab from "@/components/UndoFab";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import axios from "axios";

// Import pages
import Dashboard from "@/pages/Dashboard";
import DatabaseSync from "@/pages/DatabaseSync";
import DataGrouping from "@/pages/DataGrouping";
import MiniAssignments from "@/pages/MiniAssignments";
import ResourceCenter from "@/pages/ResourceCenter";
import InvoiceGenerator from "@/pages/InvoiceGenerator";
import Exceptions from "@/pages/Exceptions";
import ExportCenter from "@/pages/ExportCenter";
import EmailCenter from "@/pages/EmailCenter";
import ActivityLog from "@/pages/ActivityLog";
import FinancialDashboard from "@/pages/FinancialDashboard";
import ProjectDashboard from "@/pages/ProjectDashboard";
import InvoiceBatches from "@/pages/InvoiceBatches";
import LockedInvoicesAnalytics from "@/pages/LockedInvoicesAnalytics";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import PortalControl from "@/pages/PortalControl";

import Login from "@/pages/Login";
import TranslatorDashboard from "@/pages/translator/TranslatorDashboard";
import TranslatorLogin from "@/pages/translator/TranslatorLogin";
import TranslatorLayout from "@/pages/translator/TranslatorLayout";
import TranslatorMissingProject from "@/pages/translator/TranslatorMissingProject";
import ProjectClaimRequests from "@/pages/ProjectClaimRequests";
import DatabaseAdjustments from "@/pages/DatabaseAdjustments";
import ProjectTracker from "@/pages/ProjectTracker";
import TrackerLogin from "@/pages/tracker/TrackerLogin";
import TrackerDashboard from "@/pages/tracker/TrackerDashboard";
import TrackerAdmin from "@/pages/TrackerAdmin";
import ResourceOps from "@/pages/ResourceOps";
import ProjectPriceBuilder from "@/pages/ProjectPriceBuilder";
import AdvancedPriceRules from "@/pages/AdvancedPriceRules";
import PublicSnapshot from "@/pages/PublicSnapshot";
import OwnerDashboard from "@/pages/OwnerDashboard";
import MonthlyInvoices from "@/pages/MonthlyInvoices";
import BankAccounts from "@/pages/BankAccounts";
import CompanyBanking from "@/pages/CompanyBanking";
import { TrackerAuthProvider, useTrackerAuth } from "@/contexts/TrackerAuthContext";

// Icons from lucide-react
import {
  LayoutDashboard,
  Database,
  Layers,
  FileText,
  AlertTriangle,
  Download,
  Settings as SettingsIcon,
  Menu,
  X,
  LogOut,
  User,
  Users,
  Mail,
  History,
  TrendingUp,
  FolderOpen,
  Package,
  Monitor,
  FileQuestion,
  ChevronDown,
  ChevronRight,
  BarChart3,
  FileStack,
  Send,
  Cog,
  ClipboardList,
  SlidersHorizontal,
  Crosshair,
  Sun,
  Moon,
  IndianRupee,
  Lock,
  Sparkles,
  Undo2,
  Landmark,
  CalendarDays,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    // If admin tries to access translator portal, redirect to admin dashboard
    if (user.role === "admin" && requiredRole === "translator") {
      return <Navigate to="/" replace />;
    }
    // If translator tries to access admin pages, redirect to translator portal
    if (user.role === "translator" && requiredRole === "admin") {
      return <Navigate to="/portal" replace />;
    }
  }

  return children;
};

// Admin Sidebar Navigation
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(["overview", "billing"]);

  useEffect(() => {
    // Fetch company logo
    axios.get(`${API}/settings`).then(res => {
      if (res.data?.logo_url) {
        setCompanyLogo(res.data.logo_url);
      }
    }).catch(() => {});

    // Fetch pending claims count
    axios.get(`${API}/claims/pending-count`).then(res => {
      setPendingClaimsCount(res.data.count || 0);
    }).catch(() => {});

    // Poll for pending claims every 60 seconds
    const interval = setInterval(() => {
      axios.get(`${API}/claims/pending-count`).then(res => {
        setPendingClaimsCount(res.data.count || 0);
      }).catch(() => {});
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const navGroups = [
    {
      id: "overview",
      label: "Overview",
      items: [
        { path: "/", label: "Dashboard", icon: LayoutDashboard },
        { path: "/finance", label: "Financial", icon: TrendingUp },
        { path: "/projects", label: "Projects", icon: FolderOpen },
        { path: "/tracker-admin", label: "Tracker Monitor", icon: Crosshair },
        { path: "/resource-ops", label: "Resource Ops", icon: Users },
      ]
    },
    {
      id: "data",
      label: "Data Management",
      items: [
        { path: "/sync", label: "Database Sync", icon: Database },
        { path: "/adjustments", label: "Database Center", icon: Database },
        { path: "/grouping", label: "Tasks", icon: Layers },
        { path: "/mini-assignments", label: "Mini Assignments", icon: ClipboardList },
        { path: "/resource-center", label: "Resource Center", icon: Users },
        { path: "/project-pricing", label: "Project Pricing", icon: IndianRupee },
        { path: "/advanced-pricing", label: "Advanced Rules", icon: Sparkles },
      ]
    },
    {
      id: "billing",
      label: "Billing & Invoices",
      items: [
        { path: "/batches", label: "Invoice Batches", icon: Package },
        { path: "/locked-invoices-analytics", label: "Locked Analytics", icon: Lock },
        { path: "/invoices", label: "Invoice Generator", icon: FileText },
        { path: "/monthly-invoices", label: "Monthly Payments", icon: CalendarDays },
        { path: "/bank-accounts", label: "Bank Accounts", icon: Landmark },
        { path: "/claims", label: "Claim Requests", icon: FileQuestion, badge: pendingClaimsCount },
      ]
    },
    {
      id: "tools",
      label: "Tools & Reports",
      items: [
        { path: "/exceptions", label: "Exceptions", icon: AlertTriangle },
        { path: "/export", label: "Export Center", icon: Download },
        { path: "/email", label: "Email Center", icon: Mail },
        { path: "/audit-log", label: "Audit Log & Undo", icon: Undo2 },
        { path: "/activity", label: "Activity Log", icon: History },
      ]
    },
    {
      id: "settings",
      label: "Settings",
      items: [
        { path: "/portal-control", label: "Portal Control", icon: Monitor },
        { path: "/company-banking", label: "Company Banking", icon: Landmark },
        { path: "/settings", label: "Settings", icon: SettingsIcon },
      ]
    },
  ];

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  // Auto-expand group containing active path
  useEffect(() => {
    navGroups.forEach(group => {
      if (group.items.some(item => location.pathname === item.path)) {
        if (!expandedGroups.includes(group.id)) {
          setExpandedGroups(prev => [...prev, group.id]);
        }
      }
    });
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: dark ? "#0a0c12" : "#0f172a" }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
            <div className="flex items-center gap-2.5">
              {companyLogo ? (
                <img src={companyLogo} alt="Company Logo" className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-sm font-bold text-white tracking-wide" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>GRAPHIKPRO</h1>
                <p className="text-[10px] text-slate-500 tracking-wide">Invoice Maker</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <div className="space-y-4">
              {navGroups.map((group) => {
                const isExpanded = expandedGroups.includes(group.id);
                const hasActiveItem = group.items.some(item => location.pathname === item.path);
                
                return (
                  <div key={group.id}>
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                        hasActiveItem ? "text-blue-400" : "text-slate-500 hover:text-slate-300"
                      }`}
                    >
                      {group.label}
                      {isExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <ul className="mt-1 space-y-0.5">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <li key={item.path}>
                              <NavLink
                                to={item.path}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                                  isActive
                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                                }`}
                                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                              >
                                <Icon className="w-4 h-4" />
                                {item.label}
                                {item.badge > 0 && (
                                  <span className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold min-w-[18px] text-center">
                                    {item.badge}
                                  </span>
                                )}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* User Info & Theme & Logout */}
          <div className="px-3 py-3 border-t border-slate-700/50">
            {user && (
              <div className="flex items-center justify-between px-2 py-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-300 truncate max-w-[110px]">{user.display_name || user.email}</p>
                    <p className="text-[10px] text-slate-500">Admin</p>
                  </div>
                </div>
                <button
                  onClick={toggleTheme}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                  data-testid="theme-toggle-btn"
                  title={dark ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {dark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
                </button>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-slate-700/50">
            <p className="text-[10px] text-slate-600 text-center">
              &copy; 2026 Graphikpro
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

// Admin Main Layout
const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { dark } = useTheme();

  return (
    <div className="min-h-screen transition-colors duration-200" style={{ background: dark ? "#0b0e14" : "#f8fafc" }}>
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      {/* Mobile header */}
      <div
        className={`lg:hidden fixed top-0 left-0 right-0 z-30 border-b ${dark ? "bg-[#0b0e14] border-[#1e2231]" : "bg-white border-slate-200"}`}
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)", paddingLeft: "calc(env(safe-area-inset-left, 0px) + 1rem)", paddingRight: "calc(env(safe-area-inset-right, 0px) + 1rem)", paddingBottom: "0.5rem" }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className={`p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${dark ? "text-slate-400 hover:text-white hover:bg-[#1e2231]" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}
            data-testid="open-sidebar-btn"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className={`text-base sm:text-lg font-bold font-['Manrope'] ${dark ? "text-slate-200" : "text-slate-900"}`}>GRAPHIKPRO</h1>
          <div className="w-11" />
        </div>
      </div>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen overflow-x-hidden">
        <div className="pb-6 admin-main-content">
          {children}
        </div>
      </main>
      <UndoFab />
    </div>
  );
};

// Login Route Wrapper - redirects if already logged in
const LoginRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    if (user.role === "admin") {
      return <Navigate to="/" replace />;
    } else {
      return <Navigate to="/portal" replace />;
    }
  }
  
  return <Login />;
};

// Translator Portal Login Route
const TranslatorLoginRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    if (user.role === "translator") {
      return <Navigate to="/portal" replace />;
    } else if (user.role === "admin") {
      return <Navigate to="/" replace />;
    }
  }
  
  return <TranslatorLogin />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Admin Login */}
      <Route path="/login" element={<LoginRoute />} />
      
      {/* Translator Portal Login */}
      <Route path="/portal/login" element={<TranslatorLoginRoute />} />
      
      {/* Translator Portal (Protected) */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute requiredRole="translator">
            <TranslatorLayout>
              <TranslatorDashboard />
            </TranslatorLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/missing-project"
        element={
          <ProtectedRoute requiredRole="translator">
            <TranslatorLayout>
              <TranslatorMissingProject />
            </TranslatorLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Dashboard (Protected) */}
      <Route
        path="/"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><Dashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><FinancialDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ProjectDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sync"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><DatabaseSync /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/adjustments"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><DatabaseAdjustments /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grouping"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><DataGrouping /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mini-assignments"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><MiniAssignments /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={<Navigate to="/resource-center" replace />}
      />
      <Route
        path="/resource-center"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ResourceCenter /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resource/:resourceEmail"
        element={<Navigate to="/resource-center" replace />}
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><InvoiceGenerator /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly-invoices"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><MonthlyInvoices /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-accounts"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><BankAccounts /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-banking"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><CompanyBanking /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batches"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><InvoiceBatches /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locked-invoices-analytics"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><LockedInvoicesAnalytics /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AuditLog /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exceptions"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><Exceptions /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ExportCenter /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><EmailCenter /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ActivityLog /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><Settings /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-control"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><PortalControl /></AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/claims"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ProjectClaimRequests /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tracker-admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><TrackerAdmin /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resource-ops"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ResourceOps /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-pricing"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ProjectPriceBuilder /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/advanced-pricing"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdvancedPriceRules /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tracker"
        element={
          <TrackerProtectedRoute>
            <TrackerDashboard />
          </TrackerProtectedRoute>
        }
      />
      <Route path="/tracker/login" element={<TrackerLogin />} />
      <Route path="/public/tracker/:token" element={<PublicSnapshot />} />
      <Route path="/owner" element={<OwnerDashboard />} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function TrackerProtectedRoute({ children }) {
  const { user, loading } = useTrackerAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/tracker/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <TrackerAuthProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </TrackerAuthProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
